import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { READINESS_LABELS } from "@/pages/hr/payroll/payrollUtils";

export default function PayrollReadinessTab() {
  const [shadow, setShadow] = useState(null);
  const [prod, setProd] = useState(null);

  useEffect(() => {
    api.get("/hr/payroll/readiness/shadow").then((r) => setShadow(r.data)).catch(() => {});
    api.get("/hr/payroll/readiness/production").then((r) => setProd(r.data)).catch(() => {});
  }, []);

  const ok = (v) => v ? <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" /> : <AlertTriangle className="w-4 h-4 text-amber-500 inline" />;

  return (
    <div data-testid="payroll-readiness-tab" className="space-y-5">
      {/* 2027 Production Readiness */}
      <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="production-readiness">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">2027 ERP Payroll Production Readiness</h3>
          {prod && (prod.production_ready
            ? <Badge className="bg-emerald-600" data-testid="production-ready-badge"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />READY</Badge>
            : <Badge variant="outline" className="text-rose-700 border-rose-300" data-testid="production-ready-badge"><XCircle className="w-3.5 h-3.5 mr-1" />NOT READY</Badge>)}
        </div>
        {prod && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            {Object.entries(prod.checks).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b border-slate-100 py-1" data-testid={`readiness-check-${k}`}>
                <span className="text-slate-600">{READINESS_LABELS[k] || k}</span>{ok(v)}
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-500 mt-3">{prod?.note}</p>
      </div>

      {/* Shadow readiness per employee */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">Shadow Payroll Readiness (active employees)</h3>
          {shadow && <Badge variant="outline">{shadow.ready_count} / {shadow.total_active} ready</Badge>}
        </div>
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="excel-table w-full" data-testid="shadow-readiness-table">
            <thead><tr><th>Employee</th><th>Profile</th><th>Eligible</th><th>Basic Salary</th><th>Statutory</th><th>Recurring</th><th>Att. Allowance</th><th>Attendance</th><th>Leave</th><th>Ready</th><th>Missing</th></tr></thead>
            <tbody>
              {(shadow?.employees || []).map((e) => (
                <tr key={e.employee_id} data-testid={`readiness-row-${e.employee_id}`}>
                  <td><span className="font-mono text-xs">{e.employee_id}</span> {e.full_name}</td>
                  <td className="text-center">{ok(e.has_profile)}</td>
                  <td className="text-center">{ok(e.payroll_eligible)}</td>
                  <td className="text-center">{ok(e.basic_salary_set)}</td>
                  <td className="text-center">{ok(e.statutory_setup)}</td>
                  <td className="text-center text-xs">{e.recurring_components}</td>
                  <td className="text-center">{e.attendance_allowance_assignment ? "✓" : "—"}</td>
                  <td className="text-center">{ok(e.attendance_setup)}</td>
                  <td className="text-center">{ok(e.leave_setup)}</td>
                  <td className="text-center">{e.ready_for_shadow ? <Badge variant="outline" className="text-emerald-700 border-emerald-300">Ready</Badge> : <Badge variant="outline" className="text-amber-700 border-amber-300">Not ready</Badge>}</td>
                  <td className="text-xs text-rose-600">{(e.missing || []).join(", ")}</td>
                </tr>
              ))}
              {shadow && shadow.employees.length === 0 && <tr><td colSpan={11} className="text-center text-slate-400 py-6">No active employees.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
